/* Inner pages: fixed menu toggle + slide-in menu (mirrors hero behaviour) */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Menu,
  X,
  ChevronRight,
  Phone,
  Home,
  Building2,
  Wrench,
  Mail,
  Sun,
  Moon,
  Users,
  Snowflake,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import './PageLayout.css';

const menuItems = [
  { name: 'Home', path: '/', icon: Home },
  { name: 'Air Conditioning', path: '/air-conditioning', icon: Snowflake },
  { name: 'Building Services', path: '/building-services', icon: Building2 },
  { name: 'Property Services', path: '/property-services', icon: Wrench },
  { name: 'About', path: '/about', icon: Users },
  { name: 'Contact', path: '/contact', icon: Mail },
];

const menuVariants = {
  closed: {
    x: '-100%',
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 40,
    },
  },
  open: {
    x: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 40,
    },
  },
};

const PageLayout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="page-layout">
      <button
        type="button"
        className="page-menu-toggle"
        onClick={() => setIsMenuOpen(true)}
        aria-label="Open menu"
      >
        <Menu size={24} />
        <span>Menu</span>
      </button>

      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              className="menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
            />

            <motion.div
              className="side-menu"
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              <div className="side-menu-header">
                <img
                  src="/images/logo-nav.png"
                  alt="Fallow Building Services"
                  className="side-menu-logo"
                />
                <button
                  type="button"
                  className="menu-close-btn"
                  onClick={() => setIsMenuOpen(false)}
                  aria-label="Close menu"
                >
                  <X size={24} />
                </button>
              </div>

              <nav className="side-menu-nav">
                {menuItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={item.path}
                        className="side-menu-link"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <Icon size={20} />
                        <span>{item.name}</span>
                        <ChevronRight size={16} className="link-arrow" />
                      </Link>
                    </motion.div>
                  );
                })}
              </nav>

              <div className="side-menu-footer">
                <button type="button" onClick={toggleTheme} className="theme-toggle-btn">
                  {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
                  <span>{theme === 'light' ? 'Dark Mode' : 'Light Mode'}</span>
                </button>

                <a href="tel:02035765962" className="side-menu-phone">
                  <Phone size={18} />
                  <span>0203 576 5962</span>
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {children}
    </div>
  );
};

export default PageLayout;
